import React from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  IconButton,
  CircularProgress,
  styled,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Group as GroupIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Message as MessageIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { blue, green, red, amber, grey } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";
import RoomStatusDialog from "./RoomStatusDialog";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const RoomList = ({
  rooms = [],
  loading = false,
  pagination = {},
  onViewRoom,
  onEditRoom,
  onToggleRoomStatus,
  onPreviousPage,
  onNextPage,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRoom, setSelectedRoom] = React.useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = React.useState(false);
  const [statusAction, setStatusAction] = React.useState(null); // "activate" or "deactivate"
  const [statusLoading, setStatusLoading] = React.useState(false);

  const handleMenuOpen = (event, room) => {
    setAnchorEl(event.currentTarget);
    setSelectedRoom(room);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Only clear selectedRoom if no dialog is open
    if (!statusDialogOpen) {
      setSelectedRoom(null);
    }
  };

  const handleEditRoom = () => {
    if (selectedRoom && onEditRoom) {
      onEditRoom(selectedRoom);
    }
    handleMenuClose();
  };

  const handleToggleStatus = (isActive) => {
    if (selectedRoom) {
      setStatusAction(isActive ? "activate" : "deactivate");
      setStatusDialogOpen(true);
      // Don't close menu yet - wait for dialog completion
    }
    // Note: We don't call handleMenuClose() here anymore
  };

  const handleStatusConfirm = async (statusData) => {
    if (selectedRoom && onToggleRoomStatus) {
      setStatusLoading(true);
      try {
        await onToggleRoomStatus(selectedRoom.id, statusData);
      } finally {
        setStatusLoading(false);
        // Close everything after action is completed
        handleCloseStatusDialog();
      }
    }
  };

  const handleStatusCancel = () => {
    handleCloseStatusDialog();
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setStatusAction(null);
    handleMenuClose(); // Now close the menu
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid date";
    }
  };

  const getRoomTypeIcon = (type) => {
    return type === "group" ? (
      <GroupIcon fontSize="small" />
    ) : (
      <MessageIcon fontSize="small" />
    );
  };

  const getRoomStatusIcon = (status) => {
    return status === "public" ? (
      <PublicIcon fontSize="small" />
    ) : (
      <LockIcon fontSize="small" />
    );
  };

  const getRoomTypeLabel = (type) => {
    return type === "group" ? "Group" : "Single";
  };

  const getRoomStatusLabel = (status) => {
    return status === "public" ? "Public" : "Private";
  };

  if (loading && rooms.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && rooms.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }} elevation={3}>
        <Typography variant="h6" color="text.secondary">
          No rooms found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Try adjusting your search filters
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: grey[100] }}>
              <TableCell>Room</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Visibility</TableCell>
              <TableCell>Participants</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rooms.map((room) => (
              <StyledTableRow key={room.id}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        bgcolor: room.type === "group" ? blue[500] : green[500],
                        mr: 2,
                        width: 32,
                        height: 32,
                      }}
                    >
                      {getRoomTypeIcon(room.type)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {room.name || `Room ${room.id}`}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.7rem",
                          wordBreak: "break-all",
                          display: "block",
                          maxWidth: "200px",
                        }}
                      >
                        ID: {room.id}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getRoomTypeIcon(room.type)}
                    label={getRoomTypeLabel(room.type)}
                    size="small"
                    color={room.type === "group" ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label={
                        room.is_expired
                          ? "Expired"
                          : room.is_active
                          ? "Active"
                          : "Inactive"
                      }
                      size="small"
                      color={
                        room.is_expired
                          ? "error"
                          : room.is_active
                          ? "success"
                          : "default"
                      }
                      icon={
                        room.is_expired ? (
                          <ScheduleIcon fontSize="small" />
                        ) : undefined
                      }
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getRoomStatusIcon(room.status)}
                    label={getRoomStatusLabel(room.status)}
                    size="small"
                    color={room.status === "public" ? "success" : "warning"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {room.participant_count || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(room.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={room.is_active ? "Yes" : "No"}
                    size="small"
                    color={room.is_active ? "success" : "default"}
                    variant={room.is_active ? "filled" : "outlined"}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onViewRoom && onViewRoom(room.id)}
                      title="View Details"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, room)}
                      title="More Actions"
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            minWidth: 180,
            "& .MuiMenuItem-root": {
              px: 2,
              py: 1.5,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleEditRoom}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Room</ListItemText>
        </MenuItem>

        {selectedRoom?.is_expired ? (
          <MenuItem disabled sx={{ color: red[400] }}>
            <ListItemIcon>
              <BlockIcon fontSize="small" sx={{ color: red[400] }} />
            </ListItemIcon>
            <ListItemText>Cannot Activate (Expired)</ListItemText>
          </MenuItem>
        ) : selectedRoom?.is_active ? (
          <MenuItem
            onClick={() => handleToggleStatus(false)}
            sx={{ color: amber[700] }}
          >
            <ListItemIcon>
              <CloseIcon fontSize="small" sx={{ color: amber[700] }} />
            </ListItemIcon>
            <ListItemText>Deactivate Room</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => handleToggleStatus(true)}
            sx={{ color: green[600] }}
          >
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: green[600] }} />
            </ListItemIcon>
            <ListItemText>Activate Room</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Status Toggle Dialog */}
      <RoomStatusDialog
        open={statusDialogOpen}
        room={selectedRoom}
        action={statusAction}
        onConfirm={handleStatusConfirm}
        onCancel={handleStatusCancel}
        loading={statusLoading}
      />

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {rooms.length} of {pagination.total || 0} rooms
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            disabled={!pagination.hasPrev || loading}
            variant="outlined"
            size="small"
            onClick={onPreviousPage}
          >
            Previous
          </Button>
          {pagination.pageNo && pagination.totalPages && (
            <Typography variant="body2" sx={{ px: 2, py: 1 }}>
              Page {pagination.pageNo} of {pagination.totalPages}
            </Typography>
          )}
          <Button
            disabled={!pagination.hasNext || loading}
            variant="outlined"
            size="small"
            onClick={onNextPage}
          >
            Next
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default RoomList;
